import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cleanLegalText, LEGAL_DOCUMENT_KINDS, type LegalDocumentKind, type LegalDocumentsStore } from "@heylife/core";
import { ApiError, api, scopeQuery, useScope, type Localized } from "../api";
import { useT } from "../i18n";
import { LocalizedRichField, Msg, useFlash, type LocalizedRichFieldHandle } from "../ui";

const KIND_LABEL: Record<LegalDocumentKind, string> = {
  privacy: "admin.settings.legalPrivacy",
  imprint: "admin.settings.legalImprint",
  terms: "admin.settings.legalTerms",
};

type LegalScope = "church" | "app";

/** Datenschutz / Impressum / AGB for app (superadmin) or one church (church admin). */
export function LegalDocumentsCard({
  scope = "church",
  canEdit,
  churchName,
  listQueryKey,
  fetchPath,
  patchPath,
  titleKey = "admin.settings.legal",
}: {
  scope?: LegalScope;
  canEdit: boolean;
  churchName: string;
  listQueryKey?: readonly unknown[];
  fetchPath?: string;
  patchPath?: (kind: LegalDocumentKind) => string;
  titleKey?: string;
}) {
  const { t } = useT();
  const churchScope = useScope();
  const q = useQuery({
    queryKey: listQueryKey ?? ["admin", "legal", churchScope.churchId],
    queryFn: () =>
      api<{ legalDocuments: LegalDocumentsStore }>(
        fetchPath ?? `/admin/church/legal${scopeQuery(churchScope, "church")}`,
      ),
    enabled: scope === "app" || Boolean(churchScope.churchId || !churchScope.canPickScope),
  });
  const [editing, setEditing] = useState<LegalDocumentKind | null>(null);
  const store = q.data?.legalDocuments ?? {};

  return (
    <div className="form settings">
      <div className="card">
        <h2>{t(titleKey)}</h2>
        <div className="chips legal-kind-tabs">
          {LEGAL_DOCUMENT_KINDS.map((kind) => {
            const has = Boolean(store[kind]?.de?.trim() || store[kind]?.en?.trim());
            const locales = has ? [store[kind]?.de && "DE", store[kind]?.en && "EN"].filter(Boolean).join(" · ") : "";
            return (
              <button
                key={kind}
                type="button"
                className={`chip ${editing === kind ? "on" : ""}`}
                disabled={!canEdit}
                onClick={() => setEditing(kind)}
              >
                {t(KIND_LABEL[kind])}
                {locales ? <span className="muted"> · {locales}</span> : null}
              </button>
            );
          })}
        </div>
        {!canEdit ? <p className="muted small">{t("admin.settings.legalViewOnly")}</p> : null}
      </div>
      {editing && canEdit ? (
        <LegalEditor
          key={editing}
          kind={editing}
          churchName={churchName}
          scope={scope}
          initial={store[editing] ?? {}}
          patchPath={patchPath}
          onClose={() => setEditing(null)}
          onSaved={() => void q.refetch()}
        />
      ) : null}
    </div>
  );
}

function LegalEditor({
  kind,
  churchName,
  scope,
  initial,
  patchPath,
  onClose,
  onSaved,
}: {
  kind: LegalDocumentKind;
  churchName: string;
  scope: LegalScope;
  initial: Partial<Record<"de" | "en", string>>;
  patchPath?: (kind: LegalDocumentKind) => string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useT();
  const churchScope = useScope();
  const qc = useQueryClient();
  const [text, setText] = useState<Localized>({ de: initial.de ?? "", en: initial.en ?? "" });
  const richRef = useRef<LocalizedRichFieldHandle>(null);
  const [flash, run, busy] = useFlash();

  const save = async () => {
    const latest = richRef.current?.read() ?? text;
    setText(latest);
    const path =
      patchPath?.(kind) ??
      `/admin/church/legal/${kind}${scopeQuery(churchScope, "church")}`;
    const payload = { de: cleanLegalText(latest.de) ?? null, en: cleanLegalText(latest.en) ?? null };
    const ok = await run(async () => {
      try {
        await api(path, { method: "PATCH", json: payload });
      } catch (e) {
        if (scope === "app" && e instanceof ApiError && e.status === 403) {
          throw new Error(t("admin.settings.legalForbiddenSuper"));
        }
        throw e;
      }
    }, t("admin.common.saved"));
    if (ok) {
      void qc.invalidateQueries({ queryKey: ["admin", "legal"] });
      onSaved();
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <h2>{t(KIND_LABEL[kind])}</h2>
        {scope === "church" ? <p className="eyebrow">{t("admin.settings.legalFor", { church: churchName })}</p> : <p className="eyebrow">{t("admin.settings.legalApp")}</p>}
        <p className="muted small">{t("admin.settings.legalLocaleHint")}</p>
        <LocalizedRichField ref={richRef} value={text} onChange={setText} />
        <div className="actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn" disabled={busy} onClick={() => void save()}>
            {t("admin.common.save")}
          </button>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t("admin.common.cancel")}
          </button>
          <Msg msg={flash} />
        </div>
      </div>
    </div>
  );
}
